import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiWordfeud } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';

// Wordfeud's own English tile values — note B, C, G, P and J differ from Scrabble.
const SCORES = {a:1,e:1,i:1,o:1,u:1,l:1,n:1,r:1,s:1,t:1,d:2,g:3,m:3,b:4,c:4,f:4,h:4,p:4,v:4,w:4,y:4,k:5,x:8,j:10,q:10,z:10};

function TileDisplay({ letters }) {
  return (
    <span>
      {letters.toUpperCase().split('').map((ch, i) => (
        <span key={i} className="scrabble-tile">{ch}<sup className="scrabble-score">{SCORES[ch.toLowerCase()]||0}</sup></span>
      ))}
    </span>
  );
}

export default function WordfeudHelper() {
  const [tiles, setTiles]   = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [minScore, setMinScore] = useState(0);

  async function handleSolve(e) {
    e.preventDefault();
    if (!tiles.trim()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiWordfeud(tiles);
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  const filtered = results?.results?.filter(r => r.score >= minScore) || [];

  return (
    <div className="game-page">
      <PageSEO page="wordfeud-helper" />
      <div className="container" style={{maxWidth:750}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔠</span>
          <h1>Wordfeud Helper</h1>
          <p>Enter your Wordfeud rack and instantly find the highest-scoring words you can play — using Wordfeud's own tile values.</p>
        </div>

        <div className="solver-card">
          <div className="p-3 mb-3 rounded-3" style={{background:'#e0f2fe',border:'1px solid #38bdf8'}}>
            <p className="mb-1 fw-bold" style={{color:'#075985'}}>Rack Rules:</p>
            <ul className="mb-0 small fw-bold" style={{color:'#0369a1'}}>
              <li>Enter your 7 (or fewer) rack tiles as letters</li>
              <li>Use <strong>?</strong> for each blank tile you hold</li>
              <li>Example: <code>AEILNRT</code> or <code>AEILR?T</code></li>
            </ul>
          </div>

          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Your Rack</label>
              <input
                className="big-input"
                value={tiles}
                onChange={e => setTiles(e.target.value.replace(/[^a-zA-Z?]/g,'').toUpperCase())}
                placeholder="e.g. AEILNRT"
                maxLength={10}
                autoFocus
              />
              {tiles && <div className="mt-2"><TileDisplay letters={tiles.replace(/\?/g,'')} /></div>}
            </div>
            <button type="submit" className="solve-btn" disabled={loading || !tiles}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Finding…</> : '🔠 Find Words'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <p className="result-count mb-0">
                Found <strong>{filtered.length}</strong> playable word{filtered.length !== 1 ? 's' : ''}
              </p>
              <div className="d-flex align-items-center gap-2">
                <small className="fw-bold text-muted">Min score:</small>
                {[0,5,10,15,20].map(s => (
                  <button key={s} type="button"
                    className="btn btn-sm fw-bold"
                    style={{
                      borderRadius:'50px', width:'36px', height:'36px', padding:0,
                      background: minScore === s ? '#0ea5e9' : 'var(--border)',
                      color: minScore === s ? '#fff' : 'var(--text)', border:'none',
                      fontFamily:'Nunito,sans-serif'
                    }}
                    onClick={() => setMinScore(s)}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover" style={{fontFamily:'Nunito,sans-serif'}}>
                <thead>
                  <tr>
                    <th className="fw-bold">Word</th>
                    <th className="fw-bold">Score</th>
                    <th className="fw-bold">Length</th>
                    <th className="fw-bold">Blanks Used</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 50).map(r => (
                    <tr key={r.word}>
                      <td>
                        <DefinableWord word={r.word} className="fw-bold" style={{fontSize:'1.05rem', letterSpacing:1, textTransform:'uppercase'}}>
                          {r.word}
                        </DefinableWord>
                      </td>
                      <td>
                        <span className="badge rounded-pill" style={{background:'#e0f2fe',color:'#075985',fontSize:'0.85rem',fontWeight:800}}>
                          {r.score} pts
                        </span>
                      </td>
                      <td><span className="text-muted fw-bold">{r.length}</span></td>
                      <td>
                        {r.wildcards_used > 0
                          ? <span className="badge" style={{background:'#ede9fe',color:'#5b21b6'}}>{r.wildcards_used} blank</span>
                          : <span className="text-muted">—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      <SeoContent game="wordfeud-helper" />
      </div>
    </div>
  );
}
